import { useState, useEffect, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";

import { SortOrder } from "../../service/utils/constants";
import { DOWNLOAD_DOCUMENT_FILE } from "../../service/graphql/query";
import {
  base64ToBlob,
  retrieveFileType,
  splitName,
} from "../../service/utils/functions";
import SortableTableUI from "./SortableTableUI";

const FileSaver = require("file-saver");

export interface SortSettings {
  column: number;
  order: SortOrder;
}

interface SortableTableProps {
  id: string;
  className?: string;
  style?: any;
  width: number;
  header: string[];
  data: any[][];
  standaloneTable?: boolean;
  colorArray?: any[];
  hasDownloadLinks?: boolean;
  isTestingConnection?: any;
  isAuditTable?: boolean;
  isDocumentTable?: boolean;
  isWeightingTable?: boolean;
  isTippingUser?: boolean;
  testConnection?: (id: any) => void;
  editApi?: (id: any) => void;
  deleteApi?: (id: any) => void;
  currentSort?: SortSettings;
  onSortChanged?: (col: number) => void;
  currentFilterKey?: string;
  onFilterChanged?: (val: string) => void;
  rowsPerPage?: number;
  sourceDownload?: string;
  darkMode: boolean;
}

const SortableTable = (props: SortableTableProps) => {
  const [maxRowsPerPage, setMaxRowsPerPage] = useState(() =>
    props.rowsPerPage ? props.rowsPerPage : 10
  );
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageRows, setPageRows] = useState<any[][]>([]);

  const [downloadDocFile] = useLazyQuery(DOWNLOAD_DOCUMENT_FILE);

  const [filteredData, setFilteredData] = useState<any[][]>([]);

  useEffect(() => {
    setFilteredData(props.data);
  }, [props.data]);

  const parseValueForSort = useCallback((value: any): any => {
    if (value === null || value === undefined) return value;

    const strValue = String(value).trim();

    if (/^-?\d{1,3}:\d{2}(:\d{2})?$/.test(strValue)) {
      const isNegative = strValue.startsWith("-");
      const timeStr = isNegative ? strValue.substring(1) : strValue;
      const parts = timeStr.split(":");
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parts.length > 2 ? parseInt(parts[2], 10) : 0;
      const totalSeconds = hours * 3600 + minutes * 60 + seconds;
      return isNegative ? -totalSeconds : totalSeconds;
    }

    if (strValue.startsWith("£")) {
      const numStr = strValue.substring(1).replace(/,/g, "");
      const num = parseFloat(numStr);
      return isNaN(num) ? strValue : num;
    }

    if (strValue.endsWith("%")) {
      const numStr = strValue
        .substring(0, strValue.length - 1)
        .replace(/,/g, "");
      const num = parseFloat(numStr);
      return isNaN(num) ? strValue : num;
    }

    if (/^-?\d+(\.\d+)?$/.test(strValue.replace(/,/g, ""))) {
      const num = parseFloat(strValue.replace(/,/g, ""));
      return isNaN(num) ? strValue : num;
    }

    return strValue;
  }, []);

  const [lastSortApplied, setLastSortApplied] = useState<SortSettings | null>(
    null
  );

  useEffect(() => {
    if (
      props.currentSort &&
      filteredData.length > 0 &&
      (lastSortApplied === null ||
        lastSortApplied.column !== props.currentSort.column ||
        lastSortApplied.order !== props.currentSort.order)
    ) {
      const { column, order } = props.currentSort;
      const sortedData = [...filteredData].sort((a, b) => {
        const parsedA = parseValueForSort(a[column]);
        const parsedB = parseValueForSort(b[column]);

        if (parsedA === parsedB) return 0;
        if (parsedA === null || parsedA === undefined) return -1;
        if (parsedB === null || parsedB === undefined) return 1;

        const comparison = parsedA < parsedB ? -1 : 1;
        return order === SortOrder.Ascending ? comparison : -comparison;
      });

      setLastSortApplied({ ...props.currentSort });
      setFilteredData(sortedData);
      setCurrentPage(1);
    }
  }, [props.currentSort, parseValueForSort, lastSortApplied, filteredData]);

  useEffect(() => {
    let processedData = [...props.data];
    if (
      props.standaloneTable &&
      props.currentFilterKey &&
      props.currentFilterKey.length > 0
    ) {
      processedData = processedData.filter((row) => {
        const test = row.findIndex((elem) => {
          if (elem === null || elem === undefined) return false;
          const lowerCaseElem = elem.toString().toLocaleLowerCase();
          return lowerCaseElem.includes(
            props.currentFilterKey?.toLocaleLowerCase()
          );
        });
        return test >= 0;
      });
    }

    setFilteredData(processedData);
    const tp = Math.max(1, Math.ceil(processedData.length / maxRowsPerPage));
    setTotalPages(tp);

    setCurrentPage(1);

    setLastSortApplied(null);
  }, [
    props.data,
    props.currentFilterKey,
    maxRowsPerPage,
    props.standaloneTable,
  ]);

  useEffect(() => {
    if (props.standaloneTable) {
      const maxPage = Math.max(
        1,
        Math.ceil(filteredData.length / maxRowsPerPage)
      );
      const validCurrentPage = Math.min(currentPage, maxPage);

      if (validCurrentPage !== currentPage) {
        setCurrentPage(validCurrentPage);
        return;
      }

      if (filteredData.length === 0) {
        setPageRows([]);
        return;
      }

      const startIdx = maxRowsPerPage * (validCurrentPage - 1);
      const endIdx = Math.min(startIdx + maxRowsPerPage, filteredData.length);

      if (startIdx < filteredData.length) {
        const pr = filteredData.slice(startIdx, endIdx);
        setPageRows(pr);
      } else {
        setCurrentPage(1);
      }
    } else setPageRows(props.data);
  }, [
    filteredData,
    currentPage,
    maxRowsPerPage,
    props.standaloneTable,
    props.data,
  ]);

  const downloadHandler = async (fileId: string, row: string[]) => {
    const fileCompleteName = row[2] + "";
    const split = splitName(fileCompleteName);
    const fileName = split.length > 0 ? split[0] : fileCompleteName;
    let fileExtension = props.isAuditTable
      ? props.isDocumentTable
        ? row[7]
        : row[3]
      : split.length > 0
      ? split[1]
      : "pdf";
    if (fileExtension.length <= 0)
      fileExtension = split.length > 0 ? split[1] : "pdf";
    const source = !props.isDocumentTable
      ? props.sourceDownload
      : row[row.length - 2];
    if (!fileName || !source) return;
    const docFileRes = await downloadDocFile({
      variables: {
        fileId,
        source,
      },
      context: { apiName: "middleware" },
    });
    if (!docFileRes.data?.downloadProtectedDocument) return;
    const fileType = retrieveFileType(fileExtension);
    const blob = base64ToBlob(
      docFileRes.data.downloadProtectedDocument,
      fileType
    );
    FileSaver.saveAs(blob, fileName + "." + fileExtension);
  };

  return (
    <SortableTableUI
      standaloneTable={props.standaloneTable}
      currentFilterKey={props.currentFilterKey}
      header={props.header}
      id={props.id}
      currentSort={props.currentSort}
      pageRows={pageRows}
      isAuditTable={props.isAuditTable}
      isDocumentTable={props.isDocumentTable}
      isWeightingTable={props.isWeightingTable}
      isTippingUser={props.isTippingUser}
      hasDownloadLinks={props.hasDownloadLinks}
      isTestingConnection={props.isTestingConnection}
      currentPage={currentPage}
      totalPages={totalPages}
      setMaxRowsPerPage={setMaxRowsPerPage}
      onFilterChanged={props.onFilterChanged}
      onSortChanged={props.onSortChanged}
      testConnection={props.testConnection}
      editApi={props.editApi}
      deleteApi={props.deleteApi}
      downloadHandler={downloadHandler}
      setCurrentPage={setCurrentPage}
      width={props.width}
      darkMode={props.darkMode}
      className={props.className}
      colorArray={props.colorArray}
    />
  );
};

export default SortableTable;
